/**
 * Prescription Types
 *
 * TypeScript interfaces for prescription data.
 */

// Prescription type options
export type PrescriptionType = 'fast' | 'pn' | 'kur' | 'stofnun';

// Dosage format options
export type DosageFormat = 'frequency' | 'timeOfDay' | 'perNeed';

// Delivery options
export type DeliveryType = 'lyfjagatt' | 'apotek' | 'deliver';

// Time unit for frequency
export type TimeUnit = 'dag' | 'viku' | 'manudi';

// Medication from search API
export interface Medication {
  id: number;
  name: string;
  nordic_number: string;
  version?: string;
  dose?: string;
  dose_units?: string;
  form?: string;
  quantity?: string;
  quantity_units?: string;
  price?: number;
  unit_price?: number;
  active_ingredient?: string;
  description?: string;
}

// ICD10 diagnosis code
export interface ICD10Code {
  dx_id: number;
  icd_10: string;
  name_is: string;
  name_en: string;
}

// Delivery address
export interface DeliveryAddress {
  address: string;
  postalCode: string;
  area: string;
  comment: string;
}

// Time of day dosage
export interface TimeOfDayDosage {
  morning: string;
  noon: string;
  afternoon: string;
  evening: string;
}

// Frequency dosage
export interface FrequencyDosage {
  quantity: string;
  frequency: string;
  timeUnit: TimeUnit;
}

// Per need dosage (for PN prescriptions)
export interface PerNeedDosage {
  maxPerDay: string;
  unit: string;
}

// Complete prescription form data
export interface PrescriptionFormData {
  // Type
  prescriptionType: PrescriptionType;

  // Medication
  medication: Medication | null;

  // Dosage
  dosageFormat: DosageFormat;
  timeOfDayDosage: TimeOfDayDosage;
  frequencyDosage: FrequencyDosage;
  perNeedDosage: PerNeedDosage;

  // Usage instructions
  usageInstructions: string;

  // Packaging
  packageQuantity: string;
  months: string;
  daysBetween: string;

  // Diagnosis
  diagnosis: ICD10Code | null;

  // Flags
  rationing: boolean; // Skömmtun
  substitutionAllowed: boolean; // R-merking

  // Delivery
  deliveryType: DeliveryType;
  deliveryAddress: DeliveryAddress;

  // Validity
  validFrom: string;
  validTo: string;

  // Notes
  pharmacyNotes: string;
  usedFor: string;
}

// Helper to format date as YYYY-MM-DD
const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Get default validity dates (today to one year minus one day)
const getDefaultValidityDates = () => {
  const today = new Date();
  const oneYearMinusOneDay = new Date(today);
  oneYearMinusOneDay.setFullYear(oneYearMinusOneDay.getFullYear() + 1);
  oneYearMinusOneDay.setDate(oneYearMinusOneDay.getDate() - 1);
  return {
    validFrom: formatDate(today),
    validTo: formatDate(oneYearMinusOneDay),
  };
};

// Default empty form data
export const defaultPrescriptionFormData: PrescriptionFormData = {
  prescriptionType: 'fast',
  medication: null,
  dosageFormat: 'frequency',
  timeOfDayDosage: { morning: '', noon: '', afternoon: '', evening: '' },
  frequencyDosage: { quantity: '1', frequency: '1', timeUnit: 'dag' },
  perNeedDosage: { maxPerDay: '', unit: '' },
  usageInstructions: '',
  packageQuantity: '1',
  months: '1',
  daysBetween: '0',
  diagnosis: null,
  rationing: false,
  substitutionAllowed: true,
  deliveryType: 'lyfjagatt',
  deliveryAddress: { address: '', postalCode: '', area: '', comment: '' },
  ...getDefaultValidityDates(),
  pharmacyNotes: '',
  usedFor: '',
};
